/* ===========================================================================
   DailyEase - accessibility corrections for Dawn behaviour we cannot reach
   from CSS alone.

   1. Announcement slider arrows on mobile
      daily-ease.css collapses the announcement prev/next buttons to zero
      width at <=749px using visibility:hidden. That normally removes an
      element from the accessibility tree, but Dawn's SliderComponent writes
      inline styles and aria state onto these buttons as it initialises and
      as slides rotate, which can resurrect them in the focus order. The
      result is a Tab stop on a control the customer cannot see.

      Fix: mirror the CSS breakpoint in JS and explicitly mark the buttons
      inert / tabindex="-1" / aria-hidden while they are collapsed, undoing
      it above the breakpoint. Re-applied after Dawn's own updates.

   2. Rotation pause on hover and focus
      An auto-rotating announcement that keeps moving while someone is
      reading it, or while focus sits inside it, fails WCAG 2.2 "Pause,
      Stop, Hide". Dawn's autoplay is paused on interaction here.

   Loaded with `defer`, guards every lookup, and does nothing if the
   announcement bar is absent.
   =========================================================================== */
(function () {
  'use strict';

  var MOBILE = window.matchMedia('(max-width: 749px)');

  function announcementBar() {
    return document.querySelector('.announcement-bar-slider') ||
           document.querySelector('.announcement-bar');
  }

  /* ---- 1. Keep collapsed arrows out of the focus order ------------------ */
  function syncArrowFocusability() {
    var bar = announcementBar();
    if (!bar) return;

    var arrows = bar.querySelectorAll('.slider-button');
    if (!arrows.length) return;

    var collapsed = MOBILE.matches;

    arrows.forEach(function (btn) {
      // Writing attributes unconditionally would retrigger the MutationObserver
      // below, which calls straight back into here: an infinite loop. Only
      // touch the DOM when the element is not already in the target state.
      var isHidden = btn.getAttribute('aria-hidden') === 'true';
      if (collapsed === isHidden) return;

      if (collapsed) {
        btn.setAttribute('tabindex', '-1');
        btn.setAttribute('aria-hidden', 'true');
        btn.disabled = true;
        // `inert` is ignored by older browsers, which is why tabindex and
        // aria-hidden are set as well rather than relying on it alone.
        if ('inert' in btn) btn.inert = true;
      } else {
        btn.removeAttribute('tabindex');
        btn.removeAttribute('aria-hidden');
        btn.disabled = false;
        if ('inert' in btn) btn.inert = false;
      }
    });
  }

  /* ---- 2. Pause auto-rotation on hover and keyboard focus --------------- */
  function wireRotationPause() {
    var bar = announcementBar();
    if (!bar) return;

    // Dawn exposes pause/play on the slider custom element when autoplay is on.
    var slider = bar.closest('slideshow-component') ||
                 bar.querySelector('slideshow-component') ||
                 (bar.tagName && bar.tagName.toLowerCase() === 'slideshow-component' ? bar : null);

    function pause() {
      if (slider && typeof slider.pause === 'function') slider.pause();
    }
    function play() {
      // Never resume while focus is still inside the bar.
      if (bar.contains(document.activeElement)) return;
      if (slider && typeof slider.play === 'function') slider.play();
    }

    bar.addEventListener('mouseenter', pause);
    bar.addEventListener('mouseleave', play);
    bar.addEventListener('focusin', pause);
    bar.addEventListener('focusout', play);
  }

  function init() {
    syncArrowFocusability();
    wireRotationPause();

    // Dawn re-renders slider controls after its own initialisation and on
    // each slide change, so re-assert after the current task and on mutation.
    var bar = announcementBar();
    if (bar && 'MutationObserver' in window) {
      var observer = new MutationObserver(function () {
        // Only react to attribute churn on the buttons themselves.
        syncArrowFocusability();
      });
      observer.observe(bar, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['tabindex', 'aria-hidden', 'disabled', 'style', 'class']
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Breakpoint changes must flip focusability both ways.
  if (typeof MOBILE.addEventListener === 'function') {
    MOBILE.addEventListener('change', syncArrowFocusability);
  } else if (typeof MOBILE.addListener === 'function') {
    MOBILE.addListener(syncArrowFocusability);
  }
})();
